<script setup lang="ts">
import type { Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import { useFetchDtwinOperationModel } from "~/modules/dtwin/composables/useFetchDtwinOperationModel";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import type { DynamicUIDetail } from "~/modules/dtwin/models/DynamicUIDetail.interface";
import type { DynamicUIGroup } from "~/modules/dtwin/models/DynamicUIGrouped.interface";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  dtwin?: Dtwin | null;
  dtwinModel?: DtwinModel;
  organizationCode?: number;
  deviceModelUiConfig?: DynamicUIGroup;
}>();

const { t } = useI18n();
const { can } = useAcl();

const {
  isOperationModelEnabled,
  isLoading,
  error,
  dtwinOperationModel,
  fetchDtwinOperationModel,
} = useFetchDtwinOperationModel({
  organizationCode: props.organizationCode,
  operationType: OperationModelType.CONFIG,
});

const reloadOperationList: Ref<boolean> = ref(false);
const reloadConfigurationList: Ref<boolean> = ref(false);

const canManageConfigurationOperations = computed(() =>
  can(ACLRoles.CAN_MANAGE_CONFIGURATION_OPERATION),
);

const operationFormShown = ref(false);
const operationFormModel: Ref<DtwinOperation | undefined> = ref();

function showOperationForm(operation?: DtwinOperation) {
  operationFormShown.value = true;
  operationFormModel.value = operation;
}

function doReloadOperationList() {
  reloadOperationList.value = !reloadOperationList.value;
}

function doReloadConfigurationList() {
  reloadConfigurationList.value = true;
}

onMounted(async () => {
  await fetchDtwinOperationModel(props.dtwinModel);
});
</script>

<template>
  <container-component :px="0" :py="0" :is-loading="isLoading" :error="error">
    <dtwin-form-configuration-core-information
      :dtwin="dtwin"
      :configuration-ui-config="
        deviceModelUiConfig?.details.configuration as Record<
          string,
          Record<string, DynamicUIDetail[]>
        >
      "
      :organization-code="organizationCode"
      @configuration-uploaded="doReloadConfigurationList"
    />
    <block-container
      v-if="
        isOperationModelEnabled &&
        canManageConfigurationOperations &&
        organizationCode &&
        dtwin &&
        dtwinModel &&
        dtwinOperationModel
      "
      title-top
      with-margin-bottom
    >
      <!-- use () on showOperationForm method to avoid sending pointer event as parameter to it -->
      <lxc-button
        type="primary"
        html-type="button"
        :icon="ILxcPlus"
        :title="t('dtwins.form.configuration.update.title')"
        :disabled="dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED"
        :text="t('dtwins.form.configuration.update.title')"
        @click.prevent="showOperationForm()"
      />
    </block-container>
    <block-container
      v-if="
        isOperationModelEnabled &&
        canManageConfigurationOperations &&
        organizationCode &&
        dtwin &&
        dtwinModel &&
        dtwinOperationModel
      "
      :title="t('dtwins.form.configuration.operations.title')"
      title-top
    >
      <dtwin-form-configuration-core-operation-list
        :organization-code="organizationCode"
        :dtwin="dtwin"
        :dtwin-model="dtwinModel"
        :dtwin-operation-model="dtwinOperationModel"
        :reload="reloadOperationList"
        @show-operation-form="showOperationForm"
      />
    </block-container>
    <dtwin-form-configuration-core-operation-form
      v-if="canManageConfigurationOperations && dtwin && dtwinOperationModel"
      v-model:show="operationFormShown"
      v-model:reload-configuration-list="reloadConfigurationList"
      :dtwin-uid="dtwin.uid"
      :dtwin-operation-model="dtwinOperationModel"
      :operation="operationFormModel"
      @change="doReloadOperationList"
    />
  </container-component>
</template>
